import React, { useEffect, useState } from "react"
import { AddEgresadoModalProps, IEgresados } from "../../interfaces/IEgresados"
import { CategoriaInvitado, TipoEvento } from "../../interfaces/TipoEvento"
import { Button, Col, Form, Modal, Row } from "react-bootstrap"
import { CircularProgress } from "@mui/material"
import { useApi } from "../../hooks/useApi"
import { API_ENDPOINTS } from "../../api/endpoints"
import { validateEmail, validateDniFormat } from "../../utils/validation"
import { getFieldConfig, labelInvitado } from "../../utils/tipoEventoTheme"

interface DniValidationResponse {
  dniExistentes: number[]
}

const AddEgresadoModal: React.FC<AddEgresadoModalProps> = ({
  show,
  handleClose,
  handleEgresadoAdd,
  idEvento,
  tipoEvento,
  categoriaDefault,
}) => {
  const [nombre, setNombre] = useState("")
  const [apellido, setApellido] = useState("")
  const [dni, setDni] = useState("")
  const [categoria, setCategoria] = useState<CategoriaInvitado>(
    categoriaDefault ?? CategoriaInvitado.CENA,
  )
  const [tarjetasPagadas, setTarjetasPagadas] = useState("0")
  const [tarjetasPagadasMenores, setTarjetasPagadasMenores] = useState("0")
  const [email, setEmail] = useState("")
  const [telefono, setTelefono] = useState("")
  const [menuOpcional, setMenuOpcional] = useState("")
  const [mesa, setMesa] = useState("")
  const [eventoId, setEventoId] = useState(idEvento)
  const [dniValid, setDniValid] = useState(true)
  const [dniEnUso, setDniEnUso] = useState(false)
  const API_URL = API_ENDPOINTS.EGRESADOS
  const [isSubmitting, setIsSubmitting] = useState(false)
  const api = useApi()
  const [emailValido, setEmailValido] = useState(true)
  const [loadingDni, setLoadingDni] = useState(false)

  const cfg = getFieldConfig(tipoEvento, categoria)
  const mostrarSelectorCategoria = categoriaDefault === undefined
  const labelTitulo = labelInvitado(tipoEvento)

  useEffect(() => {
    if (categoriaDefault) setCategoria(categoriaDefault)
  }, [categoriaDefault])

  const resetForm = () => {
    setNombre("")
    setApellido("")
    setDni("")
    setCategoria(categoriaDefault ?? CategoriaInvitado.CENA)
    setTarjetasPagadas("0")
    setTarjetasPagadasMenores("0")
    setEmail("")
    setMenuOpcional("")
    setMesa("")
    setTelefono("")
    setEventoId(idEvento)
    setDniEnUso(false)
    setIsSubmitting(false)
    setDniValid(true)
    setEmailValido(true)
  }

  const validateDNI = async (dniVal: string): Promise<boolean> => {
    if (tipoEvento !== TipoEvento.EGRESADOS) return false
    if (!cfg.dniRequerido) return false
    setLoadingDni(true)
    try {
      const data = await api.post<DniValidationResponse>(
        `${API_URL}/validarDNI?tipoEvento=${tipoEvento}`,
        [parseInt(dniVal)],
        { errorMessage: "Error al verificar el DNI" },
      )
      return data.dniExistentes.length > 0
    } catch {
      return false
    } finally {
      setLoadingDni(false)
    }
  }

  const handleDniChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setDni(val)
    if (!val) {
      setDniValid(true)
      setDniEnUso(false)
      return
    }
    if (tipoEvento !== TipoEvento.EGRESADOS) {
      setDniValid(true)
      setDniEnUso(false)
      return
    }
    if (!cfg.dniRequerido) {
      setDniValid(true)
      setDniEnUso(false)
      return
    }
    if (validateDniFormat(val)) {
      setDniValid(true)
      const inUso = await validateDNI(val)
      setDniEnUso(inUso)
    } else {
      setDniValid(false)
      setDniEnUso(false)
    }
  }

  useEffect(() => {
    if (email) setEmailValido(validateEmail(email))
  }, [email])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (cfg.dniMostrar && cfg.dniRequerido && !validateDniFormat(dni)) {
      setDniValid(false)
      return
    }
    if (dniEnUso) return

    if (cfg.emailMostrar && email && !validateEmail(email)) {
      setEmailValido(false)
      return
    }
    setEmailValido(true)
    setIsSubmitting(true)

    const newEgresado: IEgresados = {
      id: 0,
      nombre,
      apellido,
      dni: cfg.dniMostrar && dni ? parseInt(dni) : null,
      clave: "",
      categoria,
      tarjetasPagadas: parseInt(tarjetasPagadas) || 0,
      tarjetasPagadasMenores: cfg.tarjetasMayores ? parseInt(tarjetasPagadasMenores) || 0 : 0,
      email: cfg.emailMostrar ? email : "",
      telefono: cfg.telefonoMostrar ? telefono : "",
      menuOpcional: cfg.menuMesa ? menuOpcional : null,
      observaciones: cfg.menuMesa ? mesa : null,
      eventoId: parseInt(eventoId),
    }
    handleEgresadoAdd(newEgresado)
    handleClose()
    resetForm()
  }

  const handleCancelar = () => {
    resetForm()
    handleClose()
  }

  return (
    <Modal show={show} onHide={handleCancelar} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Agregar {labelTitulo}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md="6">
              <Form.Group className="mb-3" controlId="formNombre">
                <Form.Label>Nombre</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ingrese nombre"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col md="6">
              <Form.Group className="mb-3" controlId="formApellido">
                <Form.Label>Apellido</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ingrese apellido"
                  value={apellido}
                  onChange={(e) => setApellido(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            {cfg.dniMostrar && (
              <Col md="6">
                <Form.Group className="mb-3" controlId="formDni">
                  <Form.Label>DNI{!cfg.dniRequerido && " (opcional)"}</Form.Label>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Form.Control
                      type="number"
                      placeholder="Ingrese DNI"
                      value={dni}
                      onChange={handleDniChange}
                      required={cfg.dniRequerido}
                      isInvalid={!dniValid || dniEnUso}
                    />
                    {loadingDni && (
                      <div style={{ width: 24, height: 24, display: "flex", alignItems: "center", marginLeft: 10 }}>
                        <CircularProgress size={24} />
                      </div>
                    )}
                  </div>
                  {!dniValid && (
                    <Form.Text className="text-danger">El DNI debe tener exactamente 8 caracteres.</Form.Text>
                  )}
                  {dniEnUso && (
                    <Form.Text className="text-danger">Este DNI ya está en uso.</Form.Text>
                  )}
                </Form.Group>
              </Col>
            )}

            {mostrarSelectorCategoria ? (
              <Col md="6">
                <Form.Group className="mb-3" controlId="formCategoria">
                  <Form.Label>Categoría</Form.Label>
                  <Form.Select
                    value={categoria}
                    onChange={(e) => setCategoria(e.target.value as CategoriaInvitado)}
                  >
                    {tipoEvento !== TipoEvento.FIESTAS && (
                      <>
                        <option value={CategoriaInvitado.CENA}>Cena</option>
                        <option value={CategoriaInvitado.BAILE}>Baile</option>
                      </>
                    )}
                    <option value={CategoriaInvitado.GENERAL}>General</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            ) : cfg.tarjetasBaile ? (
              <>
                <Col md="6">
                  <Form.Group className="mb-3" controlId="formTarjetasBaile2">
                    <Form.Label>Tarjetas Baile</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="Ingrese cantidad"
                      value={tarjetasPagadas}
                      onChange={(e) => setTarjetasPagadas(e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
                {cfg.telefonoMostrar && (
                  <Col md="6">
                    <Form.Group className="mb-3" controlId="formTelefono">
                      <Form.Label>Teléfono</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ingrese teléfono"
                        value={telefono}
                        onChange={(e) => setTelefono(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                )}
              </>
            ) : tipoEvento !== TipoEvento.CUMPLEANIOS_BODAS ? (
              <Col md="6">
                <Form.Group className="mb-3" controlId="formClave">
                  <Form.Label>Contraseña</Form.Label>
                  <div style={{
                    backgroundColor: "#f8f9fa",
                    fontFamily: "monospace",
                    color: "#b0b8c1",
                    border: "1px solid #dee2e6",
                    borderRadius: "0.375rem",
                    padding: "0.375rem 0.75rem",
                    fontSize: "1rem",
                    lineHeight: "1.5",
                    cursor: "default",
                    userSelect: "none",
                  }}>
                    Se genera en el servidor
                  </div>
                </Form.Group>
              </Col>
            ) : null}
          </Row>

          {(cfg.telefonoMostrar || cfg.emailMostrar) && (
            <Row>
              {cfg.telefonoMostrar && !cfg.tarjetasBaile && (
                <Col md="6">
                  <Form.Group className="mb-3" controlId="formTelefono">
                    <Form.Label>Teléfono</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Ingrese teléfono"
                      value={telefono}
                      onChange={(e) => setTelefono(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              )}
              {cfg.emailMostrar && (
                <Col md="6">
                  <Form.Group className="mb-3" controlId="formEmail">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Ingrese email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      isInvalid={email !== "" && !emailValido}
                    />
                    <Form.Control.Feedback type="invalid">Email no válido.</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              )}
            </Row>
          )}

          {cfg.tarjetasMayores && (
            <Row>
              <Col md="6">
                <Form.Group className="mb-3" controlId="formTarjetasPagadas">
                  <Form.Label>Tarjetas Pagadas</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Ingrese cantidad"
                    value={tarjetasPagadas}
                    onChange={(e) => setTarjetasPagadas(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group className="mb-3" controlId="formTarjetasMenores">
                  <Form.Label>Tarjetas Pagadas Menores</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Ingrese cantidad"
                    value={tarjetasPagadasMenores}
                    onChange={(e) => setTarjetasPagadasMenores(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
          )}

          {cfg.tarjetasBaile && mostrarSelectorCategoria && (
            <Row>
              <Col md="6">
                <Form.Group className="mb-3" controlId="formTarjetasBaile">
                  <Form.Label>Tarjetas Baile</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Ingrese cantidad"
                    value={tarjetasPagadas}
                    onChange={(e) => setTarjetasPagadas(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
              {cfg.telefonoMostrar && (
                <Col md="6">
                  <Form.Group className="mb-3" controlId="formTelefono">
                    <Form.Label>Teléfono</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Ingrese teléfono"
                      value={telefono}
                      onChange={(e) => setTelefono(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>
          )}

          {cfg.menuMesa && (
            <Row>
              <Col md="6">
                <Form.Group className="mb-3" controlId="formMenuOpcional">
                  <Form.Label>Menú Opcional</Form.Label>
                  <Form.Control
                    as="textarea"
                    placeholder="Ingrese menú opcional"
                    value={menuOpcional}
                    onChange={(e) => setMenuOpcional(e.target.value)}
                    style={{ resize: "none" }}
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group className="mb-3" controlId="formMesa">
                  <Form.Label>Observación/Mesa</Form.Label>
                  <Form.Control
                    as="textarea"
                    placeholder="Ingrese observación/mesa"
                    value={mesa}
                    onChange={(e) => setMesa(e.target.value)}
                    style={{ resize: "none" }}
                  />
                </Form.Group>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelar}>Cancelar</Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>Agregar</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default AddEgresadoModal
